/**
 * 문자열에서 숫자만 추출하여 반환 (소수점 자릿수 제한 가능)
 * @param value - 입력 문자열
 * @param decimalPlaces - 허용할 소수점 자릿수 (0이면 정수만 허용, 기본값 0)
 */
export function parseNumber(value: string, decimalPlaces = 0): number {
  if (decimalPlaces <= 0) {
    const digits = value.replace(/\D/g, "");
    return digits === "" ? 0 : parseInt(digits, 10);
  }

  // 숫자와 소수점만 허용
  const cleaned = value.replace(/[^\d.]/g, "");

  // 소수점이 여러 개인 경우 첫 번째만 유지
  const parts = cleaned.split(".");
  const integer = parts[0] || "0";
  const decimal = parts[1]?.slice(0, decimalPlaces);

  const result = decimal !== undefined ? `${integer}.${decimal}` : integer;
  const parsed = parseFloat(result);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * 문자열/숫자 Decimal → number.
 * BE 는 금액·충전량처럼 정밀도가 중요한 값을 문자열로 직렬화한다.
 * 값이 없거나 숫자로 해석되지 않으면 null — 실패를 0 으로 볼지 "-" 로 볼지는 호출부가 정한다.
 */
export function parseDecimal(
  value: string | number | null | undefined,
): number | null {
  if (value == null) return null;
  const num = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(num) ? num : null;
}

/**
 * 개수·건수 표기. 천단위 구분자만 넣는다.
 * 소수가 섞여 들어오면 반올림한다 — 개수는 정수로만 보인다.
 */
export function formatCount(value: number): string {
  return Math.round(value).toLocaleString("ko-KR");
}

/**
 * 충전량(kWh) 포맷. 서버 정밀도(소수 셋째 자리)를 그대로 표시하고 trailing zero 는 뗀다.
 * @param value - 충전량 값
 * @param withUnit - true이면 "kWh" 단위를 붙여서 반환
 */
export function formatKwh(
  value: number | string | null | undefined,
  withUnit = false,
): string {
  const num = parseDecimal(value);
  if (num == null) return "-";

  const formatted = num.toLocaleString("ko-KR", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
  return withUnit ? `${formatted}kWh` : formatted;
}

// 금액 포맷
// - fractionDigits: 소수점 자릿수 고정. 미지정 시 0~3자리 자동
// - signed: 양수에 + 를 붙임. 반올림 결과가 0 이면 부호 없이 0원
export function formatCurrency(
  value: string | number | null | undefined,
  options?: { fractionDigits?: number; signed?: boolean },
): string {
  const num = parseDecimal(value);
  if (num == null) return "-";

  const { fractionDigits, signed } = options ?? {};
  // signed 일 때만, 반올림 후 기준으로 0 을 판정한다 — "-0.001" 이 "-0.00원" 으로 새지 않게
  const rounded =
    fractionDigits == null ? num : Number(num.toFixed(fractionDigits));
  if (signed && rounded === 0) return "0원";

  const formatted = rounded.toLocaleString("ko-KR", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  return `${signed && rounded > 0 ? "+" : ""}${formatted}원`;
}

/**
 * 분 단위 정수를 `{N}일 {N}시간 {N}분` 으로. 0 인 자리는 생략하고 전체가 0 이면 "0분".
 * 60분 이상은 시간, 24시간 이상은 일로 올린다 (충전 시간 표기 규칙).
 */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes)) return "-";
  const total = Math.max(0, Math.floor(minutes));
  if (total === 0) return "0분";

  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const mins = total % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}일`);
  if (hours > 0) parts.push(`${hours}시간`);
  if (mins > 0) parts.push(`${mins}분`);
  return parts.join(" ");
}

/** 충전카드 번호 원문 최대 길이 (표시용 "-" 제외) */
export const CARD_NUMBER_MAX_LENGTH = 16;

/**
 * 충전카드 번호 원문. 영문/숫자만 남기고 최대 길이로 자른다.
 * 입력 필드의 값 파싱과 저장/전송 값이 모두 이 형태다.
 */
export function normalizeCardNumber(value: string): string {
  return value.replace(/[^A-Za-z0-9]/g, "").slice(0, CARD_NUMBER_MAX_LENGTH);
}

/**
 * 충전카드 번호 표시 포맷 (4자리마다 "-"). 빈 값이면 빈 문자열.
 * 목록/상세/입력 필드가 모두 이 함수를 거쳐 같은 표기를 쓴다.
 */
export function formatCardNumber(cardNumber?: string | null): string {
  if (!cardNumber) return "";
  return normalizeCardNumber(cardNumber).replace(/(.{4})(?=.)/g, "$1-");
}

/** 사업자 등록번호 원문 자릿수 */
export const BUSINESS_REGISTRATION_NUMBER_LENGTH = 10;

/** 사업자 등록번호 원문. 숫자만 남기고 10자리로 자른다 */
export function normalizeBusinessRegistrationNumber(value: string): string {
  return value.replace(/\D/g, "").slice(0, BUSINESS_REGISTRATION_NUMBER_LENGTH);
}

/**
 * 사업자 등록번호 표시 포맷 (###-##-#####).
 * 서버는 하이픈 없는 원문만 주고받으므로 표시·입력이 모두 이 함수를 거친다.
 */
export function formatBusinessRegistrationNumber(
  value?: string | null,
): string {
  if (!value) return "";
  const digits = normalizeBusinessRegistrationNumber(value);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

/** 전화번호 원문. 숫자만 남긴다 */
export function normalizePhone(value: string): string {
  return value.replace(/\D/g, "");
}

/** 자리수까지 다 채운 휴대폰 번호인지. 010 은 11자리, 011·016~019 는 10~11자리 */
export function isCompleteMobilePhone(value?: string | null): boolean {
  if (!value) return false;
  return /^(010\d{8}|01[16-9]\d{7,8})$/.test(normalizePhone(value));
}

/**
 * 전화번호 표시 포맷. 자리수별로 국번 길이가 달라 분기한다.
 * 서버는 하이픈 없는 원문만 주고받으므로 표시·입력이 모두 이 함수를 거친다.
 */
export function formatPhone(value?: string | null): string {
  if (!value) return "";
  const digits = normalizePhone(value);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length === 8) return digits.replace(/(\d{4})(\d{4})/, "$1-$2");
  if (digits.length === 9) {
    return digits.replace(/(\d{2})(\d{3})(\d{4})/, "$1-$2-$3");
  }
  if (digits.length === 10) {
    return digits.startsWith("02")
      ? digits.replace(/(\d{2})(\d{4})(\d{4})/, "$1-$2-$3")
      : digits.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
  }
  return digits.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3").slice(0, 13);
}
